import { motion } from "framer-motion";
import { Compass, Palette, ClipboardList, PartyPopper } from "lucide-react";

const steps = [
  {
    n: "01",
    icon: Compass,
    title: "Discover",
    desc: "We begin by understanding your event type, ideas, style, expectations, guest experience, and budget.",
  },
  {
    n: "02",
    icon: Palette,
    title: "Design",
    desc: "We shape the concept, mood, décor direction, event flow, and the key planning details that bring it to life.",
  },
  {
    n: "03",
    icon: ClipboardList,
    title: "Organise",
    desc: "We coordinate suppliers, timelines, logistics, communication, and all event essentials, end to end.",
  },
  {
    n: "04",
    icon: PartyPopper,
    title: "Deliver",
    desc: "We ensure the event runs smoothly so you can be fully present and enjoy every moment, stress-free.",
  },
];

export default function Process() {
  return (
    <section id="process" className="relative section-pad" data-testid="process-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9 }}
          className="text-center max-w-3xl mx-auto mb-20"
        >
          <div className="flex items-center justify-center gap-3 mb-6">
            <span className="h-px w-10 bg-gold" />
            <span className="text-xs uppercase tracking-[0.3em] text-gold font-medium">
              The Journey
            </span>
            <span className="h-px w-10 bg-gold" />
          </div>
          <h2
            className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
            data-testid="process-heading"
          >
            How we bring your <span className="italic text-gold-gradient">vision</span> to life.
          </h2>
        </motion.div>

        <div className="relative grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* connecting line */}
          <div className="hidden lg:block absolute top-12 left-[12%] right-[12%] h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent" />

          {steps.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.7, delay: i * 0.12 }}
                className="relative"
                data-testid={`process-step-${i}`}
              >
                <div className="glass-gold rounded-2xl p-7 h-full hover:translate-y-[-6px] transition-transform duration-500 relative">
                  {/* big number */}
                  <div className="absolute top-4 right-5 font-serif text-6xl text-gold/10 leading-none select-none">
                    {s.n}
                  </div>

                  <div className="relative w-14 h-14 rounded-full bg-gradient-to-br from-gold/30 to-transparent border border-gold/30 flex items-center justify-center mb-5 z-10">
                    <Icon size={22} className="text-gold" strokeWidth={1.5} />
                    <div className="absolute inset-0 rounded-full bg-gold/20 blur-xl -z-10" />
                  </div>

                  <p className="text-xs uppercase tracking-[0.3em] text-gold/80 mb-2">Step {s.n}</p>
                  <h3 className="font-serif text-2xl text-ivory mb-3">{s.title}</h3>
                  <p className="text-ivory/65 text-sm leading-relaxed font-light">{s.desc}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
